import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Markdown } from '@tiptap/markdown';
import * as S from './styles';

interface MarkdownEditorProps {
	content: string;
	editable?: boolean;
	onChange?: (markdown: string) => void;
	placeholder?: string;
}

// Use a `key` prop on this component to reset it when navigating
// to different content (e.g. a different guideline). The `content`
// prop is only used for initialization, not as a live binding.
const MarkdownEditor = ({
	content,
	editable = true,
	onChange,
	placeholder = 'Start writing...',
}: MarkdownEditorProps) => {
	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: { levels: [1, 2, 3, 4] },
			}),
			Placeholder.configure({ placeholder }),
			Table.configure({ resizable: false }),
			TableRow,
			TableCell,
			TableHeader,
			Markdown,
		],
		content,
		contentType: 'markdown',
		editable,
		onUpdate: ({ editor, transaction }) => {
			// @tiptap/markdown re-sets content after editor creation, firing onUpdate
			// with docChanged:false. Those aren't real edits and must not round-trip to disk.
			if (!transaction.docChanged) return;
			if (onChange) onChange(editor.getMarkdown());
		},
	});

	useEffect(() => {
		if (editor) {
			editor.setEditable(editable);
		}
	}, [editor, editable]);

	if (!editor) return null;

	return (
		<S.Base>
			<EditorContent editor={editor} />
		</S.Base>
	);
};

export default MarkdownEditor;
